import React from "react";
import {
  ToastsContainer,
  ToastsContainerPosition,
  ToastsStore,
} from "react-toasts";
import { callApi } from "../../utils/api";

interface SubtypeSetting {
  id: number;
  doc_type_key: string;
  label: string;
  is_enabled: number;
}

interface CategorySetting {
  id: number;
  file_doc_type: string;
  label: string;
  is_enabled: number;
  subtypes: SubtypeSetting[];
}

interface AllProps {
  history: any;
}

interface State {
  categories: CategorySetting[];
  search: { [key: string]: string };
  loading: boolean;
  saving: boolean;
}

class VerificationDocumentSettings extends React.Component<AllProps, State> {
  state: State = {
    categories: [],
    search: {},
    loading: true,
    saving: false,
  };

  componentDidMount() {
    this.loadSettings();
  }

  loadSettings = () => {
    this.setState({ loading: true });
    callApi("GET", "ai_verification_doc_settings_get.php")
      .then((response) => {
        const body = response && response.data;
        if (!body || !body.success || !Array.isArray(body.data)) {
          this.setState({ loading: false, categories: [] });
          ToastsStore.error((body && body.message) || "Unable to load verification documents");
          return;
        }
        this.setState({ loading: false, categories: body.data });
      })
      .catch(() => {
        this.setState({ loading: false, categories: [] });
        ToastsStore.error("Unable to load verification documents");
      });
  };

  setCategoryEnabled = (fileDocType: string, enabled: boolean) => {
    this.setState((prev) => ({
      categories: prev.categories.map((category) =>
        category.file_doc_type === fileDocType
          ? { ...category, is_enabled: enabled ? 1 : 0 }
          : category
      ),
    }));
  };

  setSubtypeEnabled = (fileDocType: string, subtypeId: number, enabled: boolean) => {
    this.setState((prev) => ({
      categories: prev.categories.map((category) => {
        if (category.file_doc_type !== fileDocType) return category;
        return {
          ...category,
          subtypes: category.subtypes.map((subtype) =>
            subtype.id === subtypeId ? { ...subtype, is_enabled: enabled ? 1 : 0 } : subtype
          ),
        };
      }),
    }));
  };

  setAllSubtypes = (fileDocType: string, enabled: boolean) => {
    this.setState((prev) => ({
      categories: prev.categories.map((category) => {
        if (category.file_doc_type !== fileDocType) return category;
        return {
          ...category,
          subtypes: category.subtypes.map((subtype) => ({ ...subtype, is_enabled: enabled ? 1 : 0 })),
        };
      }),
    }));
  };

  setSearch = (fileDocType: string, value: string) => {
    this.setState((prev) => ({ search: { ...prev.search, [fileDocType]: value } }));
  };

  saveSettings = () => {
    const userRaw = localStorage.getItem("user");
    let updatedBy = "";
    if (userRaw) {
      try {
        updatedBy = JSON.parse(userRaw).userId || "";
      } catch (_err) {
        updatedBy = "";
      }
    }
    const payload = {
      updated_by: updatedBy,
      categories: this.state.categories.map((category) => ({
        id: category.id,
        file_doc_type: category.file_doc_type,
        is_enabled: category.is_enabled ? 1 : 0,
        subtypes: category.subtypes.map((subtype) => ({
          id: subtype.id,
          doc_type_key: subtype.doc_type_key,
          is_enabled: subtype.is_enabled ? 1 : 0,
        })),
      })),
    };
    this.setState({ saving: true });
    callApi("POST", "ai_verification_doc_settings_save.php", payload)
      .then((response) => {
        const body = response && response.data;
        this.setState({ saving: false });
        if (!body || !body.success) {
          ToastsStore.error((body && body.message) || "Unable to save verification documents");
          return;
        }
        ToastsStore.success("Verification documents saved");
        this.loadSettings();
      })
      .catch(() => {
        this.setState({ saving: false });
        ToastsStore.error("Unable to save verification documents");
      });
  };

  render() {
    const { categories, search, loading, saving } = this.state;

    return (
      <section className="pcoded-main-container">
        <ToastsContainer store={ToastsStore} position={ToastsContainerPosition.TOP_RIGHT} />
        <div className="pcoded-wrapper">
          <div className="pcoded-content">
            <div className="pcoded-inner-content">
              <div className="main-body">
                <div className="page-wrapper">
                  <div className="row">
                    <div className="col-sm-12">
                      <div className="card">
                        <div className="card-header">
                          <h5>Verification Documents</h5>
                          <button
                            className="btn btn-primary"
                            style={{ float: "right" }}
                            onClick={() => this.props.history.goBack()}
                          >
                            Back
                          </button>
                        </div>
                        <div className="card-body">
                          <p className="text-muted">
                            Choose which Title Deed, Sale Agreement, EC, Revenue Record, and Mother
                            Document types are sent by Run Verification. Deed is not changed here.
                          </p>
                          {loading ? (
                            <div className="text-center py-4">
                              <i className="fa fa-spinner fa-spin" /> Loading...
                            </div>
                          ) : (
                            categories.map((category) => {
                              const term = (search[category.file_doc_type] || "").trim().toLowerCase();
                              const visible = category.subtypes.filter((subtype) => {
                                if (!term) return true;
                                return (
                                  subtype.label.toLowerCase().indexOf(term) !== -1 ||
                                  subtype.doc_type_key.toLowerCase().indexOf(term) !== -1
                                );
                              });
                              const selectedCount = category.subtypes.filter((subtype) => subtype.is_enabled).length;
                              return (
                                <div className="card mb-3" key={category.file_doc_type}>
                                  <div className="card-header d-flex justify-content-between align-items-center">
                                    <label className="mb-0">
                                      <input
                                        type="checkbox"
                                        className="mr-2"
                                        checked={!!category.is_enabled}
                                        onChange={(e) =>
                                          this.setCategoryEnabled(category.file_doc_type, e.target.checked)
                                        }
                                      />
                                      <strong>{category.label}</strong>
                                      <span className="text-muted ml-2">({category.file_doc_type})</span>
                                    </label>
                                    <span className="text-muted">
                                      {selectedCount} / {category.subtypes.length} selected
                                    </span>
                                  </div>
                                  <div className="card-body">
                                    <div className="d-flex mb-2">
                                      <input
                                        type="text"
                                        className="form-control form-control-sm mr-2"
                                        placeholder="Search document type"
                                        value={search[category.file_doc_type] || ""}
                                        onChange={(e) => this.setSearch(category.file_doc_type, e.target.value)}
                                      />
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary mr-2"
                                        onClick={() => this.setAllSubtypes(category.file_doc_type, true)}
                                      >
                                        All
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => this.setAllSubtypes(category.file_doc_type, false)}
                                      >
                                        None
                                      </button>
                                    </div>
                                    {visible.length === 0 ? (
                                      <p className="text-muted mb-0">No document types found.</p>
                                    ) : (
                                      <div className="row">
                                        {visible.map((subtype) => (
                                          <div className="col-md-4 mb-2" key={subtype.id}>
                                            <label className="mb-0">
                                              <input
                                                type="checkbox"
                                                className="mr-2"
                                                checked={!!subtype.is_enabled}
                                                onChange={(e) =>
                                                  this.setSubtypeEnabled(
                                                    category.file_doc_type,
                                                    subtype.id,
                                                    e.target.checked
                                                  )
                                                }
                                              />
                                              {subtype.label}
                                            </label>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          )}
                          <button
                            type="button"
                            className="btn btn-primary"
                            disabled={loading || saving || categories.length === 0}
                            onClick={this.saveSettings}
                          >
                            {saving ? "Saving..." : "Save"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }
}

export default VerificationDocumentSettings;
